import {
  OFFLINE_STORES,
  deleteOfflineRecord,
  getOfflineRecord,
  offlineStorageSupported,
  putOfflineRecord,
} from "./db";

export function offlineScope(workspacePublicId, storePublicId) {
  const workspace = String(workspacePublicId || "").trim();
  const store = String(storePublicId || "").trim();
  return workspace && store ? `${workspace}:${store}` : "";
}

export function isOfflineGrantUsable(session, now = Date.now()) {
  if (!session?.grant || !session?.grant_expires_at) return false;
  const expiry = new Date(session.grant_expires_at).getTime();
  return Number.isFinite(expiry) && expiry > now + 30_000;
}

export async function saveOfflineDeviceEnrollment({
  workspacePublicId,
  storePublicId,
  device,
  deviceSecret,
}) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) throw new Error("Workspace and store are required for Offline POS enrollment.");
  if (!device?.public_id || !deviceSecret) throw new Error("Offline POS enrollment response is incomplete.");
  const record = {
    scope,
    workspace_public_id: workspacePublicId,
    store_public_id: storePublicId,
    device_public_id: device.public_id,
    device_secret: deviceSecret,
    name: device.name || "Offline POS device",
    platform: device.platform || null,
    status: device.status || "active",
    authorization_state: "active",
    authorization_error: null,
    authorization_checked_at: new Date().toISOString(),
    enrolled_at: new Date().toISOString(),
  };
  await putOfflineRecord(OFFLINE_STORES.DEVICES, record);
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Persistence is best effort; IndexedDB remains the durable browser store.
  }
  return record;
}

export async function getOfflineDevice(workspacePublicId, storePublicId) {
  if (!offlineStorageSupported()) return null;
  const scope = offlineScope(workspacePublicId, storePublicId);
  return scope ? (await getOfflineRecord(OFFLINE_STORES.DEVICES, scope)) || null : null;
}

export async function saveOfflinePosSession({
  workspacePublicId,
  storePublicId,
  devicePublicId,
  grant,
  grantExpiresAt,
  identity,
}) {
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) throw new Error("Workspace and store are required for Offline POS session storage.");
  const record = {
    scope,
    workspace_public_id: workspacePublicId,
    store_public_id: storePublicId,
    device_public_id: devicePublicId,
    grant,
    grant_expires_at: grantExpiresAt,
    identity,
    cached_at: new Date().toISOString(),
  };
  await putOfflineRecord(OFFLINE_STORES.SESSIONS, record);
  const device = await getOfflineDevice(workspacePublicId, storePublicId);
  if (device?.device_public_id === devicePublicId) {
    await putOfflineRecord(OFFLINE_STORES.DEVICES, {
      ...device,
      status: "active",
      authorization_state: "active",
      authorization_error: null,
      authorization_http_status: null,
      authorization_checked_at: new Date().toISOString(),
    });
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:device-updated", { detail: { scope } }));
  }
  return record;
}

export async function getOfflinePosSession(workspacePublicId, storePublicId) {
  if (!offlineStorageSupported()) return null;
  const scope = offlineScope(workspacePublicId, storePublicId);
  return scope ? (await getOfflineRecord(OFFLINE_STORES.SESSIONS, scope)) || null : null;
}

export async function loadOfflineAuthSnapshot(workspacePublicId, storePublicId) {
  const [device, session] = await Promise.all([
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  if (!device || device.status !== "active" || !session) return null;
  if (session.device_public_id !== device.device_public_id) return null;
  if (!isOfflineGrantUsable(session)) return null;
  const identity = session.identity || {};
  if (
    identity.workspace?.public_id !== workspacePublicId
    || identity.store?.public_id !== storePublicId
    || !identity.user?.public_id
  ) {
    return null;
  }
  return { device, session, identity };
}

export async function clearOfflinePosSession(workspacePublicId, storePublicId) {
  if (!offlineStorageSupported()) return;
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (scope) await deleteOfflineRecord(OFFLINE_STORES.SESSIONS, scope);
}

export async function clearOfflineDeviceEnrollment(workspacePublicId, storePublicId) {
  if (!offlineStorageSupported()) return;
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return;
  await Promise.all([
    deleteOfflineRecord(OFFLINE_STORES.SESSIONS, scope),
    deleteOfflineRecord(OFFLINE_STORES.DEVICES, scope),
  ]);
}

export function offlineDeviceStateForServerFailure(httpStatus) {
  const status = Number(httpStatus || 0);
  if (status === 409) return { deviceStatus: "revoked", authorizationState: "revoked" };
  if (status === 404) return { deviceStatus: "missing", authorizationState: "missing" };
  if (status === 403) return { deviceStatus: null, authorizationState: "blocked" };
  return { deviceStatus: null, authorizationState: "error" };
}

export async function markOfflineDeviceServerFailure({
  workspacePublicId,
  storePublicId,
  httpStatus,
  message = null,
}) {
  if (!offlineStorageSupported()) return null;
  const scope = offlineScope(workspacePublicId, storePublicId);
  if (!scope) return null;
  const device = await getOfflineDevice(workspacePublicId, storePublicId);
  if (!device) return null;

  const resolved = offlineDeviceStateForServerFailure(httpStatus);
  const now = new Date().toISOString();
  const updated = {
    ...device,
    ...(resolved.deviceStatus ? { status: resolved.deviceStatus } : {}),
    authorization_state: resolved.authorizationState,
    authorization_error: message || null,
    authorization_http_status: Number(httpStatus || 0) || null,
    authorization_checked_at: now,
  };
  await putOfflineRecord(OFFLINE_STORES.DEVICES, updated);

  // A server-confirmed authorization failure must immediately prevent the
  // cached grant from being used for another offline session.  The device
  // record, secret, snapshot rows, sale history, and outbox are intentionally
  // preserved for recovery and forensic visibility.
  if ([403, 404, 409].includes(Number(httpStatus || 0))) {
    await deleteOfflineRecord(OFFLINE_STORES.SESSIONS, scope);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("offline:device-updated", { detail: { scope } }));
  }
  return updated;
}

export async function markOfflineDeviceRevokedLocally({
  workspacePublicId,
  storePublicId,
  message = "Offline POS device was revoked.",
}) {
  return markOfflineDeviceServerFailure({
    workspacePublicId,
    storePublicId,
    httpStatus: 409,
    message,
  });
}

export async function getOfflineAuthorizationStatus(workspacePublicId, storePublicId) {
  const [device, session] = await Promise.all([
    getOfflineDevice(workspacePublicId, storePublicId),
    getOfflinePosSession(workspacePublicId, storePublicId),
  ]);
  const expiry = new Date(session?.grant_expires_at || 0).getTime();
  const grantStatus = !session?.grant
    ? "missing"
    : isOfflineGrantUsable(session)
      ? "active"
      : Number.isFinite(expiry) && expiry <= Date.now() + 30_000
        ? "expired"
        : "invalid";
  return {
    device,
    session,
    deviceStatus: device?.status || "not_enrolled",
    authorizationState: device?.authorization_state || (device ? "unknown" : "not_enrolled"),
    authorizationError: device?.authorization_error || null,
    authorizationCheckedAt: device?.authorization_checked_at || null,
    grantStatus,
    grantExpiresAt: session?.grant_expires_at || null,
  };
}
